import { Card, CardContent, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ProductIcon from './ProductIcon';
import { Product, SrpVersion } from '../config/products';
import { colors, layout } from '../theme/tokens';

const StyledCard = styled(Card)({
  borderRadius: layout.cardRadius,
  border: `1px solid ${colors.border.card}`,
  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.08)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
  '&:hover': {
    transform: 'scale(1.03)',
    borderColor: colors.primary.main,
    boxShadow: '0 8px 24px rgba(0, 117, 149, 0.15)',
    '& .product-icon': {
      transform: 'scale(1.1)',
    },
  },
});

const CardBody = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '&:last-child': { paddingBottom: theme.spacing(3) },
}));

const VersionLink = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: colors.background.surface,
  borderRadius: 4,
  padding: '0 12px',
  height: layout.versionRowHeight,
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  textDecoration: 'none',
  border: '1px solid transparent',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    borderColor: colors.primary.main,
    backgroundColor: colors.background.hoverRow,
  },
  '&:last-child': { marginBottom: 0 },
}));

const VersionDot = styled('span')(({ theme }) => ({
  width: layout.versionDotSize,
  height: layout.versionDotSize,
  borderRadius: '50%',
  backgroundColor: colors.primary.main,
  flexShrink: 0,
  marginRight: theme.spacing(1.5),
}));

const ExternalLinkBox = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

interface VersionRowProps {
  version: SrpVersion;
}

const VersionRow = ({ version }: VersionRowProps) => (
  <VersionLink href={version.url} target="_blank" rel="noopener noreferrer">
    <VersionDot />
    <Typography sx={{ fontSize: '0.875rem', color: colors.text.body, fontWeight: 700, flex: 1 }}>
      {version.name}
    </Typography>
    <ExternalLinkBox>
      <OpenInNewIcon sx={{ color: colors.primary.dark, fontSize: 16 }} />
    </ExternalLinkBox>
  </VersionLink>
);

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => (
  <StyledCard>
    <CardBody>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>
        <Box className="product-icon" sx={{ transition: 'transform 0.25s ease', width: 'fit-content', flexShrink: 0 }}>
          <ProductIcon icon={product.icon} />
        </Box>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.primary.main }}>
          {product.name}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: '0.875rem', color: colors.text.primary, lineHeight: 1.6, mb: 3.5, mt: 1 }}>
        {product.description}
      </Typography>

      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          color: colors.text.secondary,
          opacity: 0.8,
          letterSpacing: '0.3px',
          mb: 1,
        }}
      >
        SRP Versions
      </Typography>

      {product.srpVersions.map((version, index) => (
        <VersionRow key={index} version={version} />
      ))}
    </CardBody>
  </StyledCard>
);

export default ProductCard;
